import { useState, useEffect } from 'react';
import { SPORT_TO_LEAGUE_ID, isIndividualSport } from '@low6-pickem/sports-config';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGetFeedFixturesQuery } from '@/api/feed/getFeedFixtures';
import { useGetFeedLeaguesQuery } from '@/api/feed';
import { Loader2, Calendar } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { formatInTimeZone } from 'date-fns-tz';
import { format } from 'date-fns';
import type { FeedFixture } from '@/api/feed/getFeedFixtures';

interface FixtureSelectionSectionProps {
  sport?: string;
  leagueId?: string;
  selectedFixtureId?: string;
  initialFixtureDate?: string;
  onSelect: (
    fixtureId: string,
    fixture: FeedFixture & { homeTeamName?: string; awayTeamName?: string }
  ) => void;
  disabled?: boolean;
  teamMap?: Map<string, { name: string; abbreviation?: string }>;
}

export function FixtureSelectionSection({
  sport,
  leagueId: providedLeagueId,
  selectedFixtureId,
  initialFixtureDate,
  onSelect,
  disabled = false,
  teamMap = new Map(),
}: FixtureSelectionSectionProps) {
  const [selectedDate, setSelectedDate] = useState(() => {
    if (initialFixtureDate) {
      return formatInTimeZone(new Date(initialFixtureDate), 'America/New_York', 'yyyy-MM-dd');
    }
    return formatInTimeZone(new Date(), 'America/New_York', 'yyyy-MM-dd');
  });

  // Internal league selection — used when the pickem has no sport configured
  const [internalLeagueId, setInternalLeagueId] = useState<string>('');

  // Derive effective league: prefer provided (from pickem sport), then internal selection
  const derivedFromSport = sport ? SPORT_TO_LEAGUE_ID[sport] : undefined;
  const effectiveLeagueId = providedLeagueId || derivedFromSport || internalLeagueId || undefined;
  const isIndividual = sport ? isIndividualSport(sport) : false;

  // Only show the league picker when the pickem doesn't already supply a league
  const showLeaguePicker = !providedLeagueId && !derivedFromSport;

  const { data: leagues = [], isLoading: isLoadingLeagues } = useGetFeedLeaguesQuery(undefined);

  // Update selected date when initialFixtureDate changes (e.g., when question data loads)
  useEffect(() => {
    if (initialFixtureDate) {
      const formattedDate = formatInTimeZone(
        new Date(initialFixtureDate),
        'America/New_York',
        'yyyy-MM-dd'
      );
      setSelectedDate(formattedDate);
    }
  }, [initialFixtureDate]);

  const { data: fixtures = [], isLoading: fixturesLoading } = useGetFeedFixturesQuery(
    effectiveLeagueId,
    undefined,
    selectedDate,
    selectedDate
  );

  const handleFixtureChange = (fixtureId: string) => {
    if (fixtureId === '__none__' || !fixtureId) {
      const emptyFixture = {
        externalId: '',
        leagueId: '',
        homeTeamId: '',
        awayTeamId: '',
        scheduledDate: '',
        status: 'scheduled' as const,
        homeTeamName: '',
        awayTeamName: '',
        homeTeamAbbreviation: '',
        awayTeamAbbreviation: '',
      };
      onSelect('', emptyFixture);
      return;
    }

    const fixture = fixtures.find(f => f.externalId === fixtureId);
    if (fixture) {
      const homeTeam = teamMap.get(fixture.homeTeamId);
      const awayTeam = teamMap.get(fixture.awayTeamId);

      const enhancedFixture = {
        ...fixture,
        homeTeamName: homeTeam?.name || fixture.homeTeamId,
        awayTeamName: awayTeam?.name || fixture.awayTeamId,
        homeTeamAbbreviation: homeTeam?.abbreviation || homeTeam?.name,
        awayTeamAbbreviation: awayTeam?.abbreviation || awayTeam?.name,
      };

      onSelect(fixtureId, enhancedFixture);
    }
  };

  return (
    <div className="space-y-4 border-t pt-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">
          {isIndividual ? 'Tournament Round Selection' : 'Fixture Selection'} (Optional)
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          {isIndividual
            ? 'Select a tournament round to tie this question to. Player stats will be graded based on the selected round.'
            : 'Select a fixture to automatically populate teams and option details. This is optional - you can still manually select teams.'}
        </p>
      </div>

      {/* League picker — only shown when pickem has no sport configured */}
      {showLeaguePicker && (
        <div className="space-y-2">
          <Label htmlFor="fixture-league">League</Label>
          <Select
            value={internalLeagueId || '__none__'}
            onValueChange={value => setInternalLeagueId(value === '__none__' ? '' : value)}
            disabled={disabled || isLoadingLeagues}
          >
            <SelectTrigger id="fixture-league">
              <SelectValue
                placeholder={isLoadingLeagues ? 'Loading leagues...' : 'Select a league'}
              />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Select a league</SelectItem>
              {leagues.map(league => (
                <SelectItem key={league.externalId} value={league.externalId}>
                  {league.name} ({league.sportId})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="fixture-date">
          <Calendar className="inline h-4 w-4 mr-1" />
          Date
        </Label>
        <Input
          id="fixture-date"
          type="date"
          value={selectedDate}
          onChange={e => setSelectedDate(e.target.value)}
          disabled={disabled || !effectiveLeagueId}
        />
      </div>

      <div className="flex flex-col gap-2">
        <Label htmlFor="fixture_id">Select Fixture</Label>
        {!effectiveLeagueId ? (
          <p className="text-sm text-muted-foreground">Select a league above to load fixtures.</p>
        ) : fixturesLoading ? (
          <div className="flex items-center gap-2 py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm text-muted-foreground">Loading fixtures...</span>
          </div>
        ) : fixtures.length === 0 ? (
          <Alert>
            <AlertDescription>
              No fixtures found for {selectedDate}. Try a different date.
            </AlertDescription>
          </Alert>
        ) : (
          <Select
            value={selectedFixtureId || '__none__'}
            onValueChange={value => {
              if (value === '__none__') {
                handleFixtureChange('');
              } else {
                handleFixtureChange(value);
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a fixture (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {fixtures.map(fixture => {
                const date = new Date(fixture.scheduledDate);
                const dateStr = format(date, 'MMM dd, h:mm a');

                // For individual sports (PGA): show "Tournament — Round N"
                // Fixture format: homeTeamName = tournament name, awayTeamName = "Round N"
                if (isIndividual && fixture.externalId.includes(':round_')) {
                  const homeTeam = teamMap.get(fixture.homeTeamId);
                  const tournamentName = homeTeam?.name || fixture.homeTeamId;
                  const roundPart = fixture.externalId.split(':round_')[1];
                  return (
                    <SelectItem key={fixture.externalId} value={fixture.externalId}>
                      {tournamentName} — Round {roundPart} ({format(date, 'EEE, MMM dd')})
                    </SelectItem>
                  );
                }

                const homeTeam = teamMap.get(fixture.homeTeamId);
                const awayTeam = teamMap.get(fixture.awayTeamId);
                const homeName = homeTeam?.name || 'Home Team';
                const awayName = awayTeam?.name || 'Away Team';

                return (
                  <SelectItem key={fixture.externalId} value={fixture.externalId}>
                    {awayName} @ {homeName} - {dateStr}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}
